import React, { useState } from "react";

import { useTranslation } from "react-i18next";
import {
	FrontendCompanyWithAdditionalInfomation,
	FrontendUserInfo,
} from "node-library/dist/portal";

import {
	ApprovalStatus,
	OrderStatus,
	UserStatus,
	SystemRole,
} from "node-library/dist/constants";
import { getStatusColors, getStatusString } from "../../../helpers/orderHelper";
import { formatCustomDate } from "node-library/dist/helpers/date_helper";

import {
	COAIcon,
	DeleteIcon,
	EditIcon,
	ViewResultIcon,
} from "../../../common-components/svgIcons";
import { NeutralColor } from "node-library/dist/colors";
import {
	BlackButton,
	CustomIconButton,
	GreenButton,
	KebabDropdownButton,
} from "../../../common-components/button";
import { Card } from "@mui/material";
import {
	getAnalystAuthorizationString,
	getUserRoleString,
	getUserStatusColors,
	getUserStatusString,
} from "../../../helpers/userHelper";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { ButtonTextLoadingAnimation } from "../../../common-components/animation/ButtonLoadingAnimation";
import {
	BatchAccessLevel,
	hasPermission,
	PermissionMap,
	Permissions,
} from "node-library/dist/permissions";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	operatorUser: FrontendUserInfo;
	user: FrontendUserInfo;
	goBackToViewUsersPage: (refresh: boolean) => void;

	setAddEditUserModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
	adminInAction: boolean;
	setEditUserPermissionModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
	setProceedAction: React.Dispatch<React.SetStateAction<() => void>>;
}

export default function UserDetailsContent(props: Props) {
	const { t: translation } = useTranslation();
	const [approvalModalOpen, setApprovalModalOpen] = useState<boolean>(false);

	const {
		company,
		goBackToViewUsersPage,
		operatorUser,
		user,
		setAddEditUserModalOpen,
		adminInAction,
		setEditUserPermissionModalOpen,
		setProceedAction,
	} = props;

	const operatorCanEditUserAccess: boolean = props.operatorUser
		? hasPermission(
				operatorUser?.role?.permissions as PermissionMap,
				Permissions.users.update
		  ) &&
		  (operatorUser.role?.accessLevel.administrativeLevel as number) >
				(user.role?.accessLevel.administrativeLevel as number)
		: false;

	const userHasLimitedBatchAccess: boolean = [
		BatchAccessLevel.LIMITED_ACCESS,
		BatchAccessLevel.ASSIGNED_ONLY,
	].includes(user.role?.accessLevel.limitedBatchAccess as BatchAccessLevel);

	let actionOptions: Array<{
		children: React.ReactNode;
	}> = operatorCanEditUserAccess
		? [
				{
					children: (
						<GreenButton
							key={`edit-user-info`}
							id={"edit-user-info"}
							onClick={() => {
								setAddEditUserModalOpen(true);
							}}
							disabled={adminInAction}
						>
							{adminInAction ? (
								<ButtonTextLoadingAnimation
									text={translation("Loading")}
								></ButtonTextLoadingAnimation>
							) : (
								translation("EditUser")
							)}
						</GreenButton>
					),
				},
		  ]
		: [];

	if (userHasLimitedBatchAccess) {
		//allow edit permission
		actionOptions.push({
			children: (
				<BlackButton
					key={`edit-analyst-permission`}
					id={"edit-analyst-permission"}
					onClick={() => {
						setEditUserPermissionModalOpen(true);
					}}
					disabled={adminInAction}
				>
					{adminInAction ? (
						<ButtonTextLoadingAnimation
							text={translation("Loading")}
						></ButtonTextLoadingAnimation>
					) : (
						translation("EditPermisson")
					)}
				</BlackButton>
			),
		});
	}
	const actionRow: React.ReactNode = (
		<div
			style={{ display: "flex", flexWrap: "wrap", gap: "10px", float: "right" }}
		>
			{actionOptions.map((option) => {
				return option.children;
			})}
		</div>
	);

	const userHeaderRow: React.ReactNode = (
		<div
			style={{
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				gap: "5px",
			}}
		>
			{/* <div
				className="typography-caption sample-status-box"
				style={{
					backgroundColor: statusColor.backgroundColor,
					border: `1px solid ${statusColor.borderColor}`,
					color: statusColor.textColor,
				}}
			>
				{statusString}
			</div> */}

			{/* <KebabDropdownButton
				menuOptions={actionOptions}
				id={`batch-more-action-menu-${order.orderBarcodeId}`}
				onClick={(e) => {}}
			></KebabDropdownButton> */}
		</div>
	);

	const userBody: React.ReactNode = (
		<div
			style={{
				marginTop: "10px",
				display: "flex",
				flexDirection: "column",
				gap: "5px",
			}}
		>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("Username")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{user.userName}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("FirstName")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{user.firstName}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("LastName")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{user.lastName}
				</div>
			</div>

			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">{translation("Role")}</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{getUserRoleString(user.role?.roleKey as SystemRole, translation)}
				</div>
			</div>

			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("Email")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{user.email}
				</div>
			</div>

			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("Locked")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{user.locked ? translation("Yes") : translation("No")}
				</div>
			</div>

			{userHasLimitedBatchAccess && (
				<>
					{user.analystRoleGroup?.groupName && (
						<div className="order-cell-body-row typography-body-02">
							<div className="sample-info-row-description">
								{translation("AuthorizationGroup")}
							</div>
							<div
								className="overflow-ellipsis-text"
								style={{ maxWidth: "50%" }}
							>
								{user.analystRoleGroup?.groupName}
							</div>
						</div>
					)}

					{company.companyTestCategories.map((testCategory) => {
						return (
							<div className="order-cell-body-row typography-body-02">
								<div className="sample-info-row-description">
									{`${testCategory.testCategoryName} ${translation(
										"Authorization"
									)}`}
								</div>
								<div
									// className="overflow-ellipsis-text"
									style={{ maxWidth: "50%" }}
								>
									{getAnalystAuthorizationString(
										testCategory,
										user,
										translation
									)}
								</div>
							</div>
						);
					})}
				</>
			)}

			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("RegistrationTime")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{formatCustomDate(user.inserted as Date)}
				</div>
			</div>
		</div>
	);

	return (
		<div>
			<CustomIconButton
				id="register-back-button"
				onClick={() => goBackToViewUsersPage(false)}
				icon={<KeyboardArrowLeftIcon />}
			></CustomIconButton>
			{actionRow}
			<Card
				className="common-card card-border-radius-14p card-medium-padding"
				key={user.userId}
			>
				{userHeaderRow}
				{userBody}
			</Card>
		</div>
	);
}
